import { DMF_ENTITIES, DMF_PROJECTS, type WorkCalendarSpec } from '@lg/d365-client';
import {
  defaultDimensionDisplay,
  describeAccountOverlay,
  describeJournalOverlay,
  templateLedgerFor,
  normalizeAccountOverlays,
  normalizeJournalOverlays,
  type AccountOverlay,
  type Answers,
  type JournalOverlay,
  type TemplateLedger,
} from '@lg/shared';
import type { DataPackage, EntityTable } from './dataPackage.js';
import { retargetTable } from './retargetPackage.js';
import { TEMPLATE_PROFILES, type TemplateEntity, type TemplateProfileId } from './templateProfiles.js';

/** Module access columns of Ledger fiscal calendar period V2, each "All" in the template. */
const PERIOD_ACCESS_COLUMNS = [
  'BANKACCESSLEVEL',
  'CUSTACCESSLEVEL',
  'EXPENSEACCESSLEVEL',
  'FIXEDASSETSACCESSLEVEL',
  'INVENTACCESSLEVEL',
  'ITMACCESSLEVEL',
  'LEDGERACCESSLEVEL',
  'PAYROLLACCESSLEVEL',
  'PRODACCESSLEVEL',
  'PROJECTACCESSLEVEL',
  'PURCHACCESSLEVEL',
  'RETAILACCESSLEVEL',
  'SALESACCESSLEVEL',
  'TAXACCESSLEVEL',
  'VENDACCESSLEVEL',
] as const;

/** The template's fiscal year pattern from the as-of year (the profile's years), in the template company. */
function fiscalPeriods(profileId: TemplateProfileId, asOf: Date): TemplateEntity {
  const p = TEMPLATE_PROFILES[profileId];
  const year = asOf.getUTCFullYear();
  const years = Array.from({ length: p.fiscalYears }, (_, i) => year + i);
  return {
    entityName: 'Ledger fiscal calendar period V2',
    targetEntity: 'LedgerFiscalPeriodV2Entity',
    columns: [
      'LEDGERNAME',
      'CALENDAR',
      'YEARNAME',
      'PERIODNAME',
      'LEGALENTITYID',
      'PERIODSTATUS',
      ...PERIOD_ACCESS_COLUMNS,
    ],
    rows: years.flatMap((y) =>
      p.periods.map(([name, status]) => [
        p.company,
        p.fiscalCalendar,
        String(y),
        name,
        p.company,
        status,
        ...PERIOD_ACCESS_COLUMNS.map(() => 'All'),
      ]),
    ),
  };
}

/**
 * The work calendar's working times for yesterday plus the next `days` days from the as-of date
 * (entities "Working times" and "Working time", columns as exported 2026-10-23). Yesterday is
 * included so a journal submitted around midnight, or in a time zone behind UTC, still finds open
 * time. Dates are written like the export: yyyy-mm-dd 00:00:00.
 */
function workingTimes(profileId: TemplateProfileId, asOf: Date): TemplateEntity[] {
  const w = TEMPLATE_PROFILES[profileId].workingTimes;
  const start = Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate()) - 86_400_000;
  const dates = Array.from(
    { length: w.days + 1 },
    (_, i) => `${new Date(start + i * 86_400_000).toISOString().slice(0, 10)} 00:00:00`,
  );
  return [
    {
      entityName: 'Working times',
      columns: ['WORKCALENDARID', 'CALENDARDATE', 'ISPICKINGUPCLOSED', 'WORKINGDAYDEFINITION'],
      rows: dates.map((d) => [w.calendar, d, 'No', 'Open']),
    },
    {
      entityName: 'Working time',
      columns: ['WORKCALENDARID', 'WORKCALENDARDATE', 'STARTTIME', 'EFFICIENCYPERCENTAGE', 'ENDTIME'],
      rows: dates.map((d) => [w.calendar, d, w.start, w.efficiency, w.end]),
    },
  ];
}

const seconds = (hms: string): number => {
  const [h = 0, m = 0, s = 0] = hms.split(':').map(Number);
  return h * 3600 + m * 60 + s;
};

/**
 * The template's work calendar (the profile's Calendar row: 24hr for USMF) for a company, as
 * provisioning creates it over OData with the legal entity.
 */
export function workCalendarSpec(companyId: string, profileId: TemplateProfileId = 'USMF'): WorkCalendarSpec {
  const e = TEMPLATE_PROFILES[profileId].entities.find((x) => x.entityName === 'Calendar');
  const row = e?.rows[0];
  if (!e || !row) throw new Error(`Template profile ${profileId} has no Calendar row`);
  const v = (col: string): string => row[e.columns.indexOf(col)] ?? '';
  const day = (d: string): boolean => v(`IS${d}WORKINGDAY`) === 'Yes';
  return {
    companyId: companyId.toUpperCase(),
    calendarId: v('CALENDARID'),
    name: v('CALENDARNAME'),
    workHours: Number(v('WORKHOURS')),
    startSeconds: seconds(v('DEFAULTSTARTINGTIME')),
    endSeconds: seconds(v('DEFAULTENDINGTIME')),
    workingDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map(day),
  };
}

export interface LedgerOverlays {
  journals: readonly JournalOverlay[];
  accounts: readonly AccountOverlay[];
}

/** The request's ledger overlays, from its answers (or a run's answers snapshot). */
export function ledgerOverlaysOf(answers: Answers): LedgerOverlays {
  return {
    journals: normalizeJournalOverlays(answers['ledger.journals']),
    accounts: normalizeAccountOverlays(answers['ledger.accountOverrides']),
  };
}

/** The journal names a journal overlay copies its settings from (a daily general journal). */
const OVERLAY_JOURNAL_BASE = 'GenJrn';

/**
 * Journal Control V2 rows for the journal overlays: each new journal may post only to main
 * accounts in its range, under every account structure of the template ledger. Written in the
 * template company and retargeted like the other entities. VERIFY ACCOUNTTYPE "Ledger" and
 * SEGMENT "MainAccount" against an export with rows (docs/VERIFY.md).
 */
function journalControls(profileId: TemplateProfileId, journals: readonly JournalOverlay[]): TemplateEntity {
  const p = TEMPLATE_PROFILES[profileId];
  const ledger = p.entities.find((e) => e.entityName === 'Ledger');
  const row = ledger?.rows[0] ?? [];
  // Statistical / Budget structures carry no main accounts a journal posts to.
  const structures = ['ACCOUNTSTRUCTURENAME1', 'ACCOUNTSTRUCTURENAME2']
    .map((c) => row[ledger?.columns.indexOf(c) ?? -1] ?? '')
    .filter(Boolean);
  const def = DMF_ENTITIES.journalControls;
  return {
    entityName: def.entityName,
    targetEntity: def.targetEntity,
    columns: def.columns,
    rows: journals.flatMap((j) =>
      structures.map((s) => [
        j.name,
        'Ledger',
        p.company,
        p.company,
        s,
        'MainAccount',
        j.fromAccount,
        j.toAccount,
      ]),
    ),
  };
}

/**
 * The template entity with the request's overlays applied, before retargeting:
 * - Journal names: one row per new journal, copied from the template's GenJrn (daily, its voucher
 *   series) with the new name and description.
 * - Main account overrides: the template's row for the account changed, or a new row, with
 *   ISSUSPENDED and the default dimension for this company only.
 */
function withOverlays(
  e: TemplateEntity,
  overlays: LedgerOverlays | undefined,
  ledger: TemplateLedger,
): TemplateEntity {
  if (!overlays) return e;
  const col = (c: string): number => e.columns.indexOf(c);
  const set = (row: readonly string[], values: Record<string, string>): string[] =>
    row.map((v, i) => values[e.columns[i] ?? ''] ?? v);
  if (e.entityName === 'Journal names' && overlays.journals.length) {
    const base = e.rows.find((r) => r[col('NAME')] === OVERLAY_JOURNAL_BASE);
    if (!base) return e;
    return {
      ...e,
      rows: [
        ...e.rows,
        ...overlays.journals.map((j) => set(base, { NAME: j.name, DESCRIPTION: j.description })),
      ],
    };
  }
  if (e.entityName === 'Main account legal entity overrides' && overlays.accounts.length) {
    // The overlay's dimension goes in its own slot; the row's other dimensions stay.
    const values = (a: AccountOverlay, existing = ''): Record<string, string> => ({
      ISSUSPENDED: a.suspend ? 'Yes' : 'No',
      ...(a.defaultDimensionValue
        ? { DEFAULTDIMENSIONDISPLAYVALUE: defaultDimensionDisplay(a.defaultDimensionValue, ledger, existing) }
        : {}),
    });
    const byAccount = new Map(overlays.accounts.map((a) => [a.mainAccountId, a]));
    const blank = e.rows.find((r) => r[col('DEFAULTDIMENSIONDISPLAYVALUE')] === '') ?? e.rows[0];
    const changed = e.rows.map((r) => {
      const a = byAccount.get(r[col('MAINACCOUNTID')] ?? '');
      return a ? set(r, values(a, r[col('DEFAULTDIMENSIONDISPLAYVALUE')] ?? '')) : [...r];
    });
    const added = overlays.accounts
      .filter((a) => !e.rows.some((r) => r[col('MAINACCOUNTID')] === a.mainAccountId))
      .flatMap((a) =>
        blank
          ? [set(blank, { MAINACCOUNTID: a.mainAccountId, FIXEDDIMENSIONSDISPLAYVALUE: '', ...values(a) })]
          : [],
      );
    return { ...e, rows: [...changed, ...added] };
  }
  return e;
}

/** The package's notes on the overlays, in words. */
function overlayNotes(overlays: LedgerOverlays | undefined, to: string, ledger: TemplateLedger): string[] {
  if (!overlays || (!overlays.journals.length && !overlays.accounts.length)) return [];
  return [
    `Ledger overlays for ${to} only (the template company and other venues are not touched): ` +
      [
        ...overlays.journals.map(describeJournalOverlay),
        ...overlays.accounts.map((a) => describeAccountOverlay(a, ledger)),
      ].join('; ') +
      '.',
  ];
}

/**
 * The new company's ledger setup, built from a stored template profile (no export upload): the
 * template's rows run through the same rules as a retargeted export. Company columns get the new
 * ID, the ledger is named after the company with its legal name as description, budget control off,
 * number sequences start at their smallest number, and every journal name imports with the
 * Approval workflow box off (those that had a workflow are listed to relink). The request's ledger
 * overlays (new journal names with journal control, main account overrides) are applied on top.
 */
export function buildTemplateLedgerPackage(
  companyId: string,
  opts: { legalName?: string; asOf: Date; profile?: TemplateProfileId; overlays?: LedgerOverlays },
): DataPackage {
  const profileId = opts.profile ?? 'USMF';
  const profile = TEMPLATE_PROFILES[profileId];
  const ledger = templateLedgerFor(profile.company);
  const to = companyId.toUpperCase();
  const entities: EntityTable[] = [];
  const relink: string[] = [];
  const add = (e: TemplateEntity): void => {
    const r = retargetTable(
      [[...e.columns], ...e.rows.map((row) => [...row])],
      profile.company,
      to,
      e.entityName,
      {
        ledgerDescription: opts.legalName?.trim() || to,
      },
    );
    relink.push(...r.report.relink);
    entities.push({
      entityName: e.entityName,
      fileName: `${e.entityName}.xlsx`,
      ...(e.targetEntity ? { targetEntity: e.targetEntity } : {}),
      sequence: entities.length + 1,
      columns: r.header,
      rows: r.body,
    });
  };
  for (const e of profile.entities) {
    add(withOverlays(e, opts.overlays, ledger));
    // Working times need their calendar; period status needs the ledger; journal control needs
    // its journal names.
    if (e.entityName === 'Calendar') for (const t of workingTimes(profileId, opts.asOf)) add(t);
    if (e.entityName === 'Ledger') add(fiscalPeriods(profileId, opts.asOf));
    if (e.entityName === 'Journal names' && opts.overlays?.journals.length)
      add(journalControls(profileId, opts.overlays.journals));
  }
  const year = opts.asOf.getUTCFullYear();
  const years = profile.fiscalYears > 1 ? `${year}–${year + profile.fiscalYears - 1}` : String(year);
  return {
    id: 'ledger',
    definitionGroup: DMF_PROJECTS.ledger,
    title: `Ledger setup from ${profile.company}`,
    description: `The ledger setup a test journal needs, preconfigured from ${profile.title} for ${to}: 24hr working times from yesterday through the next ${profile.workingTimes.days} days, ledger, fiscal periods ${years}, GL number sequences, journal names, ledger parameters and main-account overrides.`,
    companyContext: to,
    entities,
    notes: [
      `Import in company ${to} after the legal entity exists, before the workflows and the test journal.`,
      'Budget control is off, number sequences start at their smallest number, and journal names import with the Approval workflow box off.',
      ...(relink.length
        ? [`After importing the workflows, relink and switch approval back on for: ${relink.join(', ')}.`]
        : []),
      ...overlayNotes(opts.overlays, to, ledger),
    ],
  };
}
